import { createSelector } from 'reselect';

import { isEntityScopedChecklistTargetType } from 'constants/checklists';

import { selectLoanApplication } from './loanApplication.selectors';
import { selectLoanApplicationChecklistResultsList } from './loanApplicationChecklistResults.selectors';

// The ids of the loan's HIDDEN completeness checklists — the default drivers of the in-editor
// "Needed" highlighting when the company has not designated one (DEV-4603). See
// `getCompletenessChecklistIdsAction`. FH-3004: with the flat read on, this same slice is
// populated from the mount read's own payload (see `getChecklistsExecutionSnapshotsAction`)
// instead of a second request — the selector itself stays flag-unaware.
const selectCompletenessChecklistIds = createSelector(
  selectLoanApplication,
  ({ completenessChecklistIds }) => completenessChecklistIds,
);

// The company's DESIGNATED completeness checklist id, or null. See `getCompletenessDesignationAction`.
const selectCompletenessDesignationId = createSelector(
  selectLoanApplication,
  ({ completenessDesignationId }) => completenessDesignationId,
);

// The ids of HIDDEN entity-scoped (Borrower/Reo/Income/Asset/Liability) completeness checklists
// evaluated on this loan (Borrower: DEV-4684; other financial-info rows: DEV-5801). A completeness
// definition naturally spans loan-level fields (the designated LoanApplication checklist) AND
// per-row fields (a hidden entity-scoped companion). A hidden entity-scoped checklist exists ONLY
// to drive per-row completeness highlighting, so we identify it from the snapshots
// (an entity-scoped targetType) intersected with the loan's hidden set. These are folded into the
// designated scope below so per-row "Needed" highlighting works without a second designation slot.
const selectEntityScopedCompletenessChecklistIds = createSelector(
  selectLoanApplicationChecklistResultsList,
  selectCompletenessChecklistIds,
  (checklistResultsList, completenessChecklistIds) => {
    const hiddenIdSet = new Set(completenessChecklistIds);

    return checklistResultsList
      .filter(
        snapshot =>
          isEntityScopedChecklistTargetType(snapshot.target?.targetType) &&
          hiddenIdSet.has(snapshot.target?.checklistId),
      )
      .map(snapshot => snapshot.target.checklistId);
  },
);

/**
 * The set of checklist ids that drive the in-editor "Needed" highlighting, as a `Set` for O(1)
 * membership checks by the field/section/nav completeness selectors (DEV-4603).
 *
 * When the company has DESIGNATED a completeness checklist (`Companies.CompletenessChecklistId`),
 * that checklist drives the LOAN-level highlighting — PLUS any hidden entity-scoped completeness
 * companion, so per-row fields still highlight (the designation slot is a single loan-level
 * checklist and can't itself hold per-row items; Borrower: DEV-4684, other financial-info rows:
 * DEV-5801). Otherwise it falls back to the loan's hidden completeness checklist(s) — the global
 * master and its entity-scoped companions — so it works out of the box.
 */
export const selectCompletenessChecklistIdSet = createSelector(
  selectCompletenessChecklistIds,
  selectCompletenessDesignationId,
  selectEntityScopedCompletenessChecklistIds,
  (completenessChecklistIds, designationId, entityScopedCompletenessChecklistIds) =>
    designationId
      ? new Set([designationId, ...entityScopedCompletenessChecklistIds])
      : new Set(completenessChecklistIds),
);
